// Global app state — Svelte 5 runes shared store.
//
// Centralizes: open entry, filter (search query + active register + train-only),
// selected current (for the loom), and exposes derived helpers.

import { REG, byId, type RegId } from './data/grimoire';
import { visited } from './visited.svelte';

type RegFilter = 'all' | RegId;

function createStore() {
  let openId = $state<string | null>(null);
  let query = $state('');
  let activeReg = $state<RegFilter>('all');
  let trainOnly = $state(false);
  let activeCur = $state<string>('perception');
  let helpOpen = $state(false);
  let navSeq = $state(0); // bumped to force recompute of prev/next in Sheet

  // Strip hash when closing so back-button / share behaves like the original.
  let openViaPop = false;
  // When true, the next URL-sync effect run uses replaceState instead of
  // pushState — set by open({replace:true}) for in-modal navigation.
  let openReplace = false;
  // Scroll position before opening a modal; restored on close.
  let scrollBeforeOpen: number | null = null;

  return {
    get openId() {
      return openId;
    },
    set openId(v: string | null) {
      openId = v;
    },

    get openEntry() {
      return openId ? byId[openId] ?? null : null;
    },

    get query() {
      return query;
    },
    set query(v: string) {
      query = v;
    },

    get activeReg() {
      return activeReg;
    },
    setReg(v: RegFilter) {
      activeReg = v;
    },

    get trainOnly() {
      return trainOnly;
    },
    toggleTrain() {
      trainOnly = !trainOnly;
    },

    get activeCur() {
      return activeCur;
    },
    setCur(v: string) {
      activeCur = v;
    },

    get hasFilter() {
      return query.trim() !== '' || activeReg !== 'all' || trainOnly;
    },

    resetFilter() {
      query = '';
      activeReg = 'all';
      trainOnly = false;
    },

    // Per-entry visibility derived from current filter state.
    isVisible(id: string): boolean {
      const e = byId[id];
      if (!e) return false;
      const q = query.trim().toLowerCase();
      return (
        (activeReg === 'all' || e._reg === activeReg) &&
        (!trainOnly || !!e._train) &&
        (q === '' || (e._search || '').indexOf(q) >= 0)
      );
    },

    // Total visible entry count across all chapters.
    get visibleCount() {
      let n = 0;
      for (const r of REG)
        for (const e of r.entries) if (this.isVisible(e.id)) n++;
      return n;
    },

    get anyVisible() {
      return this.visibleCount > 0;
    },

    // Each chapter: does it have at least one visible entry?
    isChapterVisible(regId: RegId): boolean {
      const r = REG.find((x) => x.id === regId);
      if (!r) return false;
      return r.entries.some((e) => this.isVisible(e.id));
    },

    // Modal helpers (history integration lives in App.svelte).
    //
    // `replace: true` means the URL change should overwrite the current
    // history entry instead of pushing a new one. Used for in-modal navigation
    // (prev/next arrows, sibling chips): navigating across 30 entries shouldn't
    // produce 30 back-button presses.
    //
    // We also capture scrollY on open so closing can restore the position the
    // user was at before the modal hijacked the viewport — important both for
    // click-opens and for hash-loads (where the browser jumps to the element
    // behind the modal).
    open(id: string, opts: { viaPop?: boolean; replace?: boolean } = {}) {
      openViaPop = !!opts.viaPop;
      openReplace = !!opts.replace;
      // Only snapshot when opening fresh, not when navigating within modal.
      // (replace:true means we're already in a modal; preserve original.)
      if (!openId && !opts.replace) {
        scrollBeforeOpen = typeof window !== 'undefined' ? window.scrollY : 0;
      }
      openId = id;
      navSeq++;
      visited.mark(id);
    },
    close(opts: { viaPop?: boolean } = {}) {
      openViaPop = !!opts.viaPop;
      openId = null;
      // Restore the scroll position the user was at before opening. Skipped
      // when closing via popstate (browser handles it).
      if (!opts.viaPop && scrollBeforeOpen !== null && typeof window !== 'undefined') {
        const target = scrollBeforeOpen;
        scrollBeforeOpen = null;
        // Defer until after the body-lock effect releases. `auto` to bypass
        // html{scroll-behavior:smooth} so the jump is instant, not animated.
        // The body-lock release happens in the same effect tick, so we wait
        // one rAF for it to commit.
        requestAnimationFrame(() =>
          window.scrollTo({ top: target, left: 0, behavior: 'auto' }),
        );
      } else {
        scrollBeforeOpen = null;
      }
    },
    _consumeOpenViaPop() {
      const v = openViaPop;
      openViaPop = false;
      return v;
    },
    _consumeOpenReplace() {
      const v = openReplace;
      openReplace = false;
      return v;
    },
    // Replace the would-be restored scroll position with a different value.
    // Used by App.handleHashFromLoad: a share-link visitor hasn't been to the
    // page yet, so closing should land at the top, not at the (browser-jumped)
    // card position.
    _overrideScrollRestore(value: number) {
      scrollBeforeOpen = value;
    },

    // Prev/next navigation within the currently open entry's chapter,
    // respecting current filter (skips entries hidden by search/train/reg).
    get navContext() {
      // Touch navSeq so dependents re-derive when entry opens.
      void navSeq;
      const e = openId ? byId[openId] : null;
      if (!e) return null;
      const reg = e._reg as RegId;
      const ch = REG.find((r) => r.id === reg);
      if (!ch) return null;
      const visible = ch.entries.filter((x) => this.isVisible(x.id));
      const idx = visible.findIndex((x) => x.id === openId);
      const prev = idx > 0 ? visible[idx - 1].id : null;
      const next = idx >= 0 && idx < visible.length - 1 ? visible[idx + 1].id : null;
      const total = visible.length;
      const pos = idx + 1;
      return { prev, next, total, pos, reg, chTitle: ch.title };
    },

    get helpOpen() {
      return helpOpen;
    },
    toggleHelp(v?: boolean) {
      helpOpen = v ?? !helpOpen;
    },
  };
}

export const store = createStore();
